import React, { useEffect, useState } from "react";
import { Box, Button, Tab, Typography, useTheme, Grid } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Response as ResponseType,
  FormItem,
  ResponseSubject as SubjectResponseType,
  User,
  ResponseSubjectType,
  ItemType,
  ItemSubjectType,
  Campaign,
  CampaignType,
  AssignmentSubmission,
  SubmissionType,
} from "../../types";
import { ResponseSubject } from "./Subject";
import { BlingCheckmark } from "../BlingCheckmark";
import { sortedSubjects, getUserName } from "../../DataTransform";
import { SkipNext } from "@mui/icons-material";
import { ResponsePoints } from "./Points";
import isCompatible from "../SubjectType/compatibility";
import { AssignmentSubmissionPreview } from "../Assignment/SubmissionPreview";

export interface Props {
  response: ResponseType;
  author: User;
  subjects: User[];
  formItems: FormItem[];
  onChange: (value: ResponseType) => void;
  instructions: string;
  extraTopMargin?: boolean;
  campaignType?: CampaignType;
  hideRecipientIndicators?: boolean;
}

export const ResponseEdit = ({
  response,
  formItems,
  subjects,
  author,
  instructions,
  onChange,
  extraTopMargin = false,
  campaignType,
  hideRecipientIndicators,
}: Props) => {
  const theme = useTheme();

  const hasNonGeneralNonPointsItems = formItems.some(
    (item) =>
      item.itemType !== ItemType.Points &&
      item.subjectType !== ItemSubjectType.General,
  );

  const sortedReponseSubjects = sortedSubjects(
    response.responseSubjects,
    author.id,
  );

  const filteredResponseSubjects = sortedSubjects(
    hasNonGeneralNonPointsItems
      ? response.responseSubjects
      : response.responseSubjects.filter(
          (rs) => rs.subjectType === ResponseSubjectType.General,
        ),
    author.id,
  );

  //
  // State
  //

  const [tab, setTab] = useState(filteredResponseSubjects[0].id);
  const [loading, setLoading] = useState(0);

  const sortedItems = [...formItems].sort((a, b) => a.order - b.order);
  const pointsItems = sortedItems.filter(
    (item) => item.itemType === ItemType.Points,
  );

  const isLastTab =
    pointsItems.length > 0
      ? "points" === tab
      : filteredResponseSubjects[filteredResponseSubjects.length - 1].id ===
        tab;
  const currentSubject = filteredResponseSubjects.find((sr) => sr.id === tab);

  const responsePairs = filteredResponseSubjects.map((subjectResponse) => {
    const subject = subjects.find(
      (target) => target.id === subjectResponse.subjectUserId,
    );
    return {
      subject: subject!,
      subjectResponse,
    };
  });

  // Check if this is an assignment-type campaign
  const isAssignmentCampaign = campaignType === CampaignType.Assignment;

  const isLivePresentationAssignment =
    isAssignmentCampaign &&
    response.assignment?.submissionType === SubmissionType.LivePresentation;

  // Get the current subject's assignment submission for preview
  const getCurrentSubjectSubmission = () => {
    if (
      !isAssignmentCampaign ||
      !response.assignment?.submissions ||
      !currentSubject
    ) {
      console.log("getCurrentSubjectSubmission early return:", {
        isAssignmentCampaign,
        hasAssignment: !!response.assignment,
        hasAssignmentSubmissions: !!response.assignment?.submissions,
        hasCurrentSubject: !!currentSubject,
      });
      return undefined;
    }

    console.log(
      "Looking for submission for subject:",
      currentSubject.subjectUserId,
    );
    console.log(
      "Available submissions:",
      response.assignment.submissions.map((sub) => ({
        id: sub.id,
        authorId: sub.authorId,
        submissionText: sub.submissionText?.substring(0, 50) + "...",
      })),
    );

    const submission = response.assignment.submissions.find(
      (submission) => submission.authorId === currentSubject.subjectUserId,
    );

    console.log("Found submission:", submission ? "YES" : "NO");
    return submission;
  };

  const currentSubjectSubmission = getCurrentSubjectSubmission();
  const currentSubjectUser = subjects.find(
    (user) => user.id === currentSubject?.subjectUserId,
  );

  //
  // Effects
  //

  useEffect(() => {
    const currentTabExists = filteredResponseSubjects.some(
      (subject) => subject.id === tab,
    );
    if (tab !== "points" && !currentTabExists) {
      setTab(filteredResponseSubjects[0].id);
    }
  }, [filteredResponseSubjects, tab]);

  useEffect(() => {
    if (loading < 200) {
      setTimeout(() => setLoading(loading + 20), 100);
    }
  }, [loading, setLoading]);

  //
  // Event handlers
  //

  function handleSubjectResponseChange(subjectResponse: SubjectResponseType) {
    const clone = { ...response };
    if (subjectResponse.completed) {
      setLoading(0);
    }
    clone.responseSubjects = clone.responseSubjects.map((target) =>
      target.id === subjectResponse.id ? subjectResponse : target,
    );
    onChange(clone);
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  function handleNext() {
    const index = filteredResponseSubjects.findIndex((sr) => sr.id === tab);
    if (
      index + 1 >= filteredResponseSubjects.length &&
      pointsItems.length > 0
    ) {
      setTab("points");
    } else {
      setTab(filteredResponseSubjects[index + 1].id);
    }

    // Scroll to page top after tab change
    setTimeout(() => window.scroll({ top: 0, left: 0, behavior: "smooth" }), 0);
  }

  //
  // Helpers
  //

  function getName(pair: {
    subject: User;
    subjectResponse: SubjectResponseType;
  }) {
    const { subject: user, subjectResponse } = pair;

    if (subjectResponse.subjectType === ResponseSubjectType.General) {
      return "General Reflection";
    }

    return getUserName(user) + (user.id === author.id ? " (Yourself)" : "");
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <TabContext value={tab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              position: "fixed",
              top: extraTopMargin ? "90px" : "45px",
              left: "0px",
              backgroundColor: theme.palette.background.default,
              width: "100%",
              zIndex: 2,
            }}
          >
            <TabList
              value={tab}
              onChange={handleTabChange}
              aria-label="basic tabs example"
            >
              {filteredResponseSubjects.map((subjectResponse, index) => {
                const pair = {
                  subject: subjects.find(
                    (target) => target.id === subjectResponse.subjectUserId,
                  )!,
                  subjectResponse,
                };

                return (
                  <Tab
                    disabled={
                      isLivePresentationAssignment
                        ? false
                        : !pair.subjectResponse.completed &&
                          index >
                            filteredResponseSubjects.reduce(
                              (acc, response) =>
                                acc + (response.completed ? 1 : 0),
                              0,
                            )
                    }
                    key={index}
                    label={
                      isLivePresentationAssignment
                        ? getName(pair)
                        : pair.subjectResponse.completed ||
                            index <=
                              filteredResponseSubjects.reduce(
                                (acc, response) =>
                                  acc + (response.completed ? 1 : 0),
                                0,
                              )
                          ? getName(pair)
                          : ""
                    }
                    value={pair.subjectResponse.id}
                    icon={
                      <BlingCheckmark
                        active={
                          tab === pair.subjectResponse.id &&
                          !pair.subjectResponse.completed
                        }
                        user={pair.subject}
                        completed={pair.subjectResponse.completed}
                        divProps={{ style: { marginRight: 10 } }}
                        size="small"
                      />
                    }
                    iconPosition="start"
                  />
                );
              })}
              {pointsItems.length > 0 && (
                <Tab
                  disabled={
                    filteredResponseSubjects.length >
                    filteredResponseSubjects.reduce(
                      (acc, response) => acc + (response.completed ? 1 : 0),
                      0,
                    )
                  }
                  label={
                    filteredResponseSubjects.length <=
                    filteredResponseSubjects.reduce(
                      (acc, response) => acc + (response.completed ? 1 : 0),
                      0,
                    )
                      ? "Points"
                      : ""
                  }
                  value="points"
                  icon={
                    <BlingCheckmark
                      active={tab === "points"}
                      completed={false}
                      divProps={{ style: { marginRight: 10 } }}
                      size="small"
                    />
                  }
                  iconPosition="start"
                />
              )}
            </TabList>
          </Box>
          <Box sx={{ mt: 8 }}>&nbsp;</Box>
          {filteredResponseSubjects.map((subjectResponse, index) => (
            <TabPanel key={index} value={subjectResponse.id}>
              {isAssignmentCampaign ? (
                // Two-column layout for assignment campaigns
                <Grid container spacing={3}>
                  {/* Left column: Assignment Submission Preview */}
                  <Grid item xs={12} md={7}>
                    <AssignmentSubmissionPreview
                      submission={currentSubjectSubmission}
                      authorName={
                        currentSubjectUser
                          ? getUserName(currentSubjectUser)
                          : "Student"
                      }
                    />
                  </Grid>

                  {/* Right column: Response Form */}
                  <Grid item xs={12} md={5}>
                    {subjectResponse.subjectType !==
                      ResponseSubjectType.General && (
                      <>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                          Instructions:
                        </Typography>
                        <Typography sx={{ mb: 2 }}>
                          {instructions.split("\n").map((x, index, arr) => (
                            <>
                              {x}
                              {index !== arr.length - 1 && <br />}
                            </>
                          ))}
                        </Typography>
                      </>
                    )}
                    <ResponseSubject
                      subjectResponse={subjectResponse}
                      author={author}
                      subject={
                        subjects.find(
                          (target) =>
                            target.id === subjectResponse.subjectUserId,
                        )!
                      }
                      onChange={handleSubjectResponseChange}
                      formItems={sortedItems.filter(
                        (item) =>
                          item.itemType !== ItemType.Points &&
                          isCompatible(
                            subjectResponse.subjectType,
                            item.subjectType,
                            item.recipientType,
                            author,
                            response.isTeamLead,
                            subjectResponse,
                          ),
                      )}
                      hideRecipientIndicators={hideRecipientIndicators}
                    />
                  </Grid>
                </Grid>
              ) : (
                // Original single-column layout for non-assignment campaigns
                <>
                  {subjectResponse.subjectType !==
                    ResponseSubjectType.General && (
                    <>
                      <Typography variant="h5" sx={{ mb: 2 }}>
                        Instructions:
                      </Typography>
                      <Typography sx={{ mb: 2 }}>
                        {instructions.split("\n").map((x, index, arr) => (
                          <>
                            {x}
                            {index !== arr.length - 1 && <br />}
                          </>
                        ))}
                      </Typography>
                    </>
                  )}
                  <ResponseSubject
                    subjectResponse={subjectResponse}
                    author={author}
                    subject={
                      subjects.find(
                        (target) => target.id === subjectResponse.subjectUserId,
                      )!
                    }
                    onChange={handleSubjectResponseChange}
                    formItems={sortedItems.filter(
                      (item) =>
                        item.itemType !== ItemType.Points &&
                        isCompatible(
                          subjectResponse.subjectType,
                          item.subjectType,
                          item.recipientType,
                          author,
                          response.isTeamLead,
                          subjectResponse,
                        ),
                    )}
                    hideRecipientIndicators={hideRecipientIndicators}
                  />
                </>
              )}
            </TabPanel>
          ))}
          {pointsItems.length > 0 && (
            <TabPanel value="points">
              {pointsItems.map((pointsItem) => (
                <ResponsePoints
                  onChange={handleSubjectResponseChange}
                  formItem={pointsItem}
                  key={pointsItem.id}
                  subjects={subjects}
                  responseSubjects={sortedReponseSubjects}
                />
              ))}
            </TabPanel>
          )}
        </TabContext>
      </Box>
      <Button
        disabled={!currentSubject?.completed || isLastTab}
        variant="contained"
        endIcon={<SkipNext />}
        onClick={handleNext}
        sx={{ ml: 3 }}
      >
        Next
      </Button>
    </>
  );
};
